// Minimal typed HTTP client — drop-in replacement for openapi-fetch.
// Implements the API surface used in this project:
//   createClient, .GET, .POST, .PUT, .PATCH, .DELETE
//   { data, error, response } return shape
//   params: { path, query }, body support
//
// Saves ~4 kB gzip vs the full openapi-fetch library.
// See: src/lib/api/client.ts

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FetchResult<TData, TError = unknown> {
  data?: TData;
  error?: TError;
  response: Response;
}

type QueryParams = Record<
  string,
  string | number | boolean | string[] | undefined | null
>;

interface CallOptions {
  params?: {
    path?: Record<string, string | number>;
    query?: QueryParams;
  };
  body?: unknown;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
  signal?: AbortSignal;
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

// ── Path param substitution ───────────────────────────────────────────────────

const PATH_PARAM_RE = /\{([^{}]+)\}/g;

function appendQueryParam(
  params: URLSearchParams,
  k: string,
  v: string | number | boolean | string[],
): void {
  if (Array.isArray(v)) {
    for (const item of v) params.append(k, String(item));
  } else {
    params.set(k, String(v));
  }
}

function buildQueryString(query: QueryParams | undefined): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v != null) appendQueryParam(params, k, v);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildUrl(path: string, opts: CallOptions, baseUrl: string): string {
  // Substitute path params: /foo/{id} → /foo/123
  const resolved = path.replace(PATH_PARAM_RE, (_, key: string) => {
    const val = opts.params?.path?.[key];
    if (val === undefined) throw new Error(`missing path param: ${key}`);
    return String(val);
  });

  const withQuery = resolved + buildQueryString(opts.params?.query);
  // Avoid double /api/v2/api/v2 prefix
  const prefix = baseUrl.replace(/\/$/, "");
  return withQuery.startsWith(prefix) ? withQuery : `${prefix}${withQuery}`;
}

// ── Core fetch ────────────────────────────────────────────────────────────────

interface ClientConfig {
  customFetch: typeof fetch;
  baseUrl: string;
  baseCredentials: RequestCredentials;
}

function buildRequestInit(
  opts: CallOptions,
  config: ClientConfig,
): RequestInit {
  const headers: Record<string, string> = { ...opts.headers };
  let body: string | undefined;
  if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers["Content-Type"] = "application/json";
  }
  const init: RequestInit = {
    headers,
    credentials: opts.credentials ?? config.baseCredentials,
  };
  if (body !== undefined) init.body = body;
  if (opts.signal !== undefined) init.signal = opts.signal;
  return init;
}

async function parseErrorBody<TError>(
  response: Response,
): Promise<TError | undefined> {
  try {
    const text = await response.text();
    if (text) return JSON.parse(text) as TError;
  } catch {
    /* non-JSON error body */
  }
  return undefined;
}

async function resolveResponse<TData, TError>(
  response: Response,
): Promise<FetchResult<TData, TError>> {
  const isNoContent =
    response.status === 204 || response.headers.get("Content-Length") === "0";
  if (!response.ok || isNoContent) {
    const error = await parseErrorBody<TError>(response);
    return error !== undefined ? { error, response } : { response };
  }
  try {
    const data = (await response.json()) as TData;
    return { data, response };
  } catch {
    return { response };
  }
}

async function call<TData, TError = unknown>(
  method: HttpMethod,
  path: string,
  opts: CallOptions,
  config: ClientConfig,
): Promise<FetchResult<TData, TError>> {
  const url = buildUrl(path, opts, config.baseUrl);
  const init: RequestInit = { method, ...buildRequestInit(opts, config) };
  const response = await config.customFetch(url, init);
  return resolveResponse<TData, TError>(response);
}

// ── Client factory ─────────────────────────────────────────────────────────────

interface ClientOptions {
  baseUrl?: string;
  credentials?: RequestCredentials;
  fetch?: typeof fetch;
}

// TData defaults to `any` so untyped call sites (apiClient.GET("/path"))
// produce FetchResult<any> instead of FetchResult<{}>, matching the behaviour
// of the original openapi-fetch where paths inference filled in the type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ClientMethod = <TData = any, TError = unknown>(
  path: string,
  opts?: CallOptions,
) => Promise<FetchResult<TData, TError>>;

interface FetchClient {
  GET: ClientMethod;
  POST: ClientMethod;
  PUT: ClientMethod;
  PATCH: ClientMethod;
  DELETE: ClientMethod;
}

/** Create a typed HTTP client. Drop-in for openapi-fetch's createClient<paths>(). */
export default function createClient<_TPaths>(
  clientOpts?: ClientOptions,
): FetchClient {
  const config: ClientConfig = {
    baseUrl: (clientOpts?.baseUrl ?? "").replace(/\/$/, ""),
    customFetch: clientOpts?.fetch ?? globalThis.fetch,
    baseCredentials: clientOpts?.credentials ?? "same-origin",
  };

  function method(httpMethod: HttpMethod): ClientMethod {
    return (path, opts = {}) => call(httpMethod, path, opts, config);
  }

  return {
    GET: method("GET"),
    POST: method("POST"),
    PUT: method("PUT"),
    PATCH: method("PATCH"),
    DELETE: method("DELETE"),
  };
}
